import { oofDtos } from '@vectronic/openApi-customer';

/** refNos of refurbished-device articles — order items carrying one of these require device allocation. */
const REFURBISHMENT_ARTICLE_REF_NOS: string[] = ['16192', '16193', '16194', '16195'];

export function isRefurbishmentOrderItem(orderItem: oofDtos.OrderItem): boolean {
  return (orderItem.articles || []).some(
    (article) => !!article.refNo && REFURBISHMENT_ARTICLE_REF_NOS.includes(article.refNo),
  );
}

/** A canceled item needs no devices, even when it is a refurbishment item. */
export function needsDeviceAllocation(orderItem: oofDtos.OrderItem): boolean {
  return (
    isRefurbishmentOrderItem(orderItem) &&
    orderItem.status !== oofDtos.OrderItem.StatusEnum.Canceled
  );
}

/** Cancellation leaves shipped items shipped, so the order's own status decides first. */
export function needsDeviceAllocationOfOrder(order: oofDtos.Order): boolean {
  return (
    order.status !== oofDtos.Order.StatusEnum.Canceled &&
    (order.orderItems || []).some(needsDeviceAllocation)
  );
}

/** Devices a refurbishment order item still waits for before it can be configured. */
export function missingDeviceCount(orderItem: oofDtos.OrderItem): number {
  if (!needsDeviceAllocation(orderItem)) return 0;
  // An allocation without a device id names no device yet, so it fills no slot.
  const allocatedCount = (orderItem.deviceAllocations?.allocations ?? []).filter(
    ({ id }) => id != null,
  ).length;
  return Math.max(0, (orderItem.amount ?? 0) - allocatedCount);
}

/** A refurbishment order item is editable only once every device it needs has been allocated. */
export function isAllocationIncomplete(orderItem: oofDtos.OrderItem): boolean {
  return missingDeviceCount(orderItem) > 0;
}

/** Devices the whole order still waits for, across its refurbishment items. */
export function missingDeviceCountOfOrder(order: oofDtos.Order): number {
  return (order.orderItems || []).reduce((missing, item) => missing + missingDeviceCount(item), 0);
}

/** Devices the order's refurbishment items need in total, allocated or not. */
export function refurbishedDeviceCountOfOrder(order: oofDtos.Order): number {
  return (order.orderItems || [])
    .filter(needsDeviceAllocation)
    .reduce((needed, item) => needed + (item.amount ?? 0), 0);
}
